import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  EyeIcon,
  TrashIcon,
  TrophyIcon,
  CalendarIcon,
  UserGroupIcon,
  LockOpenIcon,
  LockClosedIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { SeasonService, ContestantService, LeagueService } from '../../../services/firebaseService';
import { ScoreService } from '../../../services/scoreService';
import { formatDateTime } from '../../../utils/dateUtils';
import { buildClosedWeekScoreStatusMap } from '../../../utils/weekScoreStatus';
import { isMultiEliminationWeek } from '../../../utils/predictionUtils';
import { ELIMINATION_TYPES } from '../../../models/dataModels';
import ContestantModal from './ContestantModal';
import WeekModal from './WeekModal';

export default function SeasonDetail() {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  const [season, setSeason] = useState(null);
  const [contestants, setContestants] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recalculating, setRecalculating] = useState(false);
  const [recalculationResult, setRecalculationResult] = useState(null);
  const [calculatingWeek, setCalculatingWeek] = useState(null);
  const [weekScoreStatus, setWeekScoreStatus] = useState({});
  
  // Modal states
  const [showContestantModal, setShowContestantModal] = useState(false);
  const [showWeekModal, setShowWeekModal] = useState(false);
  const [selectedContestant, setSelectedContestant] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [editingContestant, setEditingContestant] = useState(false);

  useEffect(() => {
    loadSeasonData();
  }, [seasonId]);

  const loadSeasonData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load season data
      const seasonData = await SeasonService.getSeason(seasonId);
      if (!seasonData) {
        setError('Season not found');
        return;
      }
      setSeason(seasonData);
      
      // Load contestants for this season
      const [seasonContestants, seasonLeagues] = await Promise.all([
        ContestantService.getContestantsBySeason(seasonId),
        LeagueService.getLeaguesUsingSeason(seasonId)
      ]);
      setContestants(seasonContestants);
      setLeagues(seasonLeagues);

      const closedWeeks = (seasonData.weeks || []).filter(
        w => w.isOpenForPredictions === false
      );
      const scoreStatusMap = await buildClosedWeekScoreStatusMap(
        seasonId,
        closedWeeks
      );
      setWeekScoreStatus(scoreStatusMap);
      
    } catch (error) {
      console.error('Error loading season data:', error);
      setError('Failed to load season data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddContestant = () => {
    setSelectedContestant(null);
    setEditingContestant(false);
    setShowContestantModal(true);
  };

  const handleEditContestant = (contestant) => {
    setSelectedContestant(contestant);
    setEditingContestant(true);
    setShowContestantModal(true);
  };

  const handleAddWeek = () => {
    setSelectedWeek(null);
    setShowWeekModal(true);
  };

  const handleViewWeek = (week) => {
    navigate(`/admin/seasons/${seasonId}/week/${week.weekNumber}`);
  };

  const handleViewPicks = (week) => {
    navigate(`/admin/seasons/${seasonId}/week/${week.weekNumber}/picks`);
  };

  const handleContestantSaved = () => {
    setShowContestantModal(false);
    loadSeasonData(); // Reload to get updated contestants
  };

  const handleWeekSaved = () => {
    setShowWeekModal(false);
    loadSeasonData(); // Reload to get updated weeks
  };

  const handleRecalculateScores = async () => {
    try {
      setRecalculating(true);
      setRecalculationResult(null);
      setError('');
      
      const result = await ScoreService.recalculateScoresForSeason(seasonId);
      setRecalculationResult(result);
      
      // Reload season data to show updated scores
      await loadSeasonData();
    } catch (error) {
      console.error('Error recalculating scores:', error);
      setError('Failed to recalculate scores. Please try again.');
    } finally {
      setRecalculating(false);
    }
  };

  const handleCalculateWeekScores = async (week) => {
    try {
      setCalculatingWeek(week.weekNumber);
      setError('');
      
      if (!week.results || Object.keys(week.results).length === 0) {
        setError(`No results entered for Week ${week.weekNumber}. Please enter results first.`);
        return;
      }
      
      const result = await ScoreService.recalculateScoresForSpecificWeek(
        seasonId, 
        week.weekNumber, 
        week.results, 
        week.isFinale
      );
      
      // Reload season data to show updated scores
      await loadSeasonData();
      
      // Show success message
      setRecalculationResult({
        success: true,
        teamsUpdated: result.teamsUpdated,
        leaguesProcessed: result.leaguesProcessed,
        week: week.weekNumber
      });
    } catch (error) {
      console.error('Error calculating week scores:', error);
      setError(`Failed to calculate scores for Week ${week.weekNumber}. Please try again.`);
    } finally {
      setCalculatingWeek(null);
    }
  };

  const getContestantName = (contestantId) => {
    if (!contestantId) return 'N/A';
    
    // Handle different data formats
    if (typeof contestantId === 'string') {
      const contestant = contestants.find(c => c.id === contestantId);
      return contestant ? contestant.name : contestantId;
    }
    
    // Handle array format (old format)
    if (Array.isArray(contestantId)) {
      if (contestantId.length === 0) return 'N/A';
      const contestant = contestants.find(c => c.id === contestantId[0]);
      return contestant ? contestant.name : contestantId[0];
    }
    
    return contestantId;
  };

  const renderClosedWeekScoreRow = (week) => {
    if (week.isOpenForPredictions) return null;
    const scoreStatus = weekScoreStatus[week.weekNumber] || {
      calculated: false
    };
    const hasResults =
      week.results && Object.keys(week.results).length > 0;
    const calculatedAtLabel = scoreStatus.calculatedAt
      ? formatDateTime(scoreStatus.calculatedAt)
      : '';
    return (
      <div className="flex flex-wrap items-center gap-3 text-xs mt-1">
        {hasResults ? (
          <span className="inline-flex items-center text-gray-500">
            <CheckCircleIcon className="h-3 w-3 mr-1 text-green-500" />
            Results entered
          </span>
        ) : (
          <span className="inline-flex items-center text-gray-400">
            <XCircleIcon className="h-3 w-3 mr-1" />
            No results
          </span>
        )}
        {scoreStatus.calculated ? (
          <span className="inline-flex items-center text-gray-500">
            <CheckCircleIcon className="h-3 w-3 mr-1 text-green-500" />
            Scores calculated
            {calculatedAtLabel ? (
              <span className="ml-1 text-gray-400">
                ({calculatedAtLabel})
              </span>
            ) : null}
          </span>
        ) : hasResults ? (
          <span className="inline-flex items-center text-yellow-600">
            <ClockIcon className="h-3 w-3 mr-1" />
            Scores not calculated
          </span>
        ) : null}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-survivor-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/admin/seasons')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Seasons
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/admin/seasons')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{season.name}</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Season ID: {season.id} • Status: {season.status}
                </p>
                <div className="mt-2">
                  {season.mergeWeek ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Merged at Week {season.mergeWeek}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      Pre-Merge
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate(`/admin/seasons/${seasonId}/tribes`)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <UserGroupIcon className="h-4 w-4 mr-2" />
                Manage Tribes
              </button>
              <button
                onClick={handleAddWeek}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Week
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 py-6 sm:px-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contestants Section */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <UserGroupIcon className="h-6 w-6 text-gray-400 mr-2" />
                    <h2 className="text-lg font-medium text-gray-900">Contestants</h2>
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {contestants.length}
                    </span>
                  </div>
                  <button
                    onClick={handleAddContestant}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-survivor-600 hover:bg-survivor-700"
                  >
                    <PlusIcon className="h-3 w-3 mr-1" />
                    Add Contestant
                  </button>
                </div>
              </div>
              <div className="px-6 py-4">
                {contestants.length === 0 ? (
                  <div className="text-center py-8">
                    <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No contestants</h3>
                    <p className="mt-1 text-sm text-gray-500">Get started by adding a contestant.</p>
                    <div className="mt-6">
                      <button
                        onClick={handleAddContestant}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Add Contestant
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {contestants.map((contestant) => (
                      <div key={contestant.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-gray-900">{contestant.name}</h3>
                          <p className="text-sm text-gray-500">{contestant.bio}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEditContestant(contestant)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title="Edit contestant"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Weeks Section */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <CalendarIcon className="h-6 w-6 text-gray-400 mr-2" />
                    <h2 className="text-lg font-medium text-gray-900">Weeks</h2>
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {season.weeks?.length || 0}
                    </span>
                  </div>
                  <button
                    onClick={handleAddWeek}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PlusIcon className="h-3 w-3 mr-1" />
                    Add Week
                  </button>
                </div>
              </div>
              <div className="px-6 py-4">
                {!season.weeks || season.weeks.length === 0 ? (
                  <div className="text-center py-8">
                    <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No weeks</h3>
                    <p className="mt-1 text-sm text-gray-500">Get started by adding a week.</p>
                    <div className="mt-6">
                      <button
                        onClick={handleAddWeek}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Add Week
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {season.weeks.map((week, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <h3 className="text-sm font-medium text-gray-900">
                              Week {week.weekNumber}
                              {week.isMergeWeek && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  Merge
                                </span>
                              )}
                              {week.isFinale && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Finale
                                </span>
                              )}
                              {week.eliminationType && (
                                <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  isMultiEliminationWeek(week)
                                    ? 'bg-purple-100 text-purple-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {week.eliminationType === ELIMINATION_TYPES.TRIPLE
                                    ? 'Triple'
                                    : week.eliminationType === ELIMINATION_TYPES.DOUBLE_SIMULTANEOUS
                                      ? 'Double (sim.)'
                                      : week.eliminationType === ELIMINATION_TYPES.DOUBLE
                                        ? 'Double'
                                        : 'Single'}
                                </span>
                              )}
                            </h3>
                            <span 
                              className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                week.isOpenForPredictions 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : week.results
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                              title={
                                week.isOpenForPredictions
                                  ? 'Open for predictions'
                                  : week.results
                                  ? 'Results entered'
                                  : 'Draft - not open for predictions'
                              }
                            >
                              {week.isOpenForPredictions ? (
                                <>
                                  <LockOpenIcon className="h-3 w-3 mr-1" />
                                  Open
                                </>
                              ) : week.results ? (
                                <>
                                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                                  Results
                                </>
                              ) : (
                                <>
                                  <LockClosedIcon className="h-3 w-3 mr-1" />
                                  Closed
                                </>
                              )}
                            </span>
                          </div>
                          <div className="mt-1 space-y-1">
                            {week.airDate && (
                              <p className="text-sm text-gray-500">
                                Air Date: {(() => {
                                  try {
                                    // Handle both Firestore Timestamp and string dates
                                    const date = week.airDate.toDate ? week.airDate.toDate() : new Date(week.airDate);
                                    return date.toLocaleDateString();
                                  } catch (error) {
                                    return 'Invalid Date';
                                  }
                                })()}
                              </p>
                            )}
                            {renderClosedWeekScoreRow(week)}
                            {week.results && Object.keys(week.results).length > 0 && (
                              <div className="text-sm text-gray-500">
                                {week.isFinale ? (
                                  <div>
                                    <span className="font-medium">Winner:</span> {getContestantName(week.results.winner)} • 
                                    <span className="font-medium ml-2">Fire-making loser:</span> {getContestantName(week.results.fireMakingLoser)} • 
                                    <span className="font-medium ml-2">Voted out:</span> {getContestantName(week.results.votedOut)}
                                  </div>
                                ) : isMultiEliminationWeek(week) && Array.isArray(week.results.votedOut) && week.results.votedOut.length > 0 && week.results.votedOut[0].position ? (
                                  <div>
                                    <span className="font-medium">Voted out:</span>
                                    <div className="ml-2 mt-1 space-y-1">
                                      {week.results.votedOut
                                        .sort((a, b) => a.position - b.position)
                                        .map(item => (
                                          <div key={`${item.position}-${item.contestantName || item.contestantId || ''}`} className="text-xs">
                                            <span className="font-medium">Voted out #{item.position}:</span> {item.contestantName || getContestantName(item.contestantId)}
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <span className="font-medium">Voted out:</span> {getContestantName(week.results.votedOut)}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewPicks(week)}
                            className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                            title="View prediction picks for this week"
                          >
                            Picks
                          </button>
                          {week.results && Object.keys(week.results).length > 0 && (
                            <button
                              onClick={() => handleCalculateWeekScores(week)}
                              disabled={calculatingWeek === week.weekNumber}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Calculate scores for this week"
                            >
                              {calculatingWeek === week.weekNumber ? (
                                <>
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                  Calculating...
                                </>
                              ) : (
                                <>
                                  <TrophyIcon className="h-3 w-3 mr-1" />
                                  Calculate Scores
                                </>
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => handleViewWeek(week)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title="View week details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Leagues Section */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <BuildingOfficeIcon className="h-6 w-6 text-gray-400 mr-2" />
                <h2 className="text-lg font-medium text-gray-900">Leagues</h2>
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {leagues.length}
                </span>
              </div>
            </div>
            <div className="px-6 py-4">
              {leagues.length === 0 ? (
                <div className="text-center py-8">
                  <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No leagues using this season
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Leagues appear here once a league manager adds this season to
                    their league.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-200 -mx-6">
                  {leagues.map((league) => (
                    <li key={league.id} className="hover:bg-gray-50 px-6 py-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center min-w-0">
                          <BuildingOfficeIcon className="h-8 w-8 text-survivor-600 flex-shrink-0" />
                          <div className="ml-4 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <Link
                                to={`/admin/leagues/${league.id}`}
                                className="text-sm font-medium text-survivor-600 truncate hover:text-survivor-700"
                              >
                                {league.name}
                              </Link>
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  league.status === 'active'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {league.status || 'unknown'}
                              </span>
                              {league.leagueSeason?.isActive ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Active season
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                  Completed season
                                </span>
                              )}
                            </div>
                            {league.description ? (
                              <p className="mt-1 text-sm text-gray-500 truncate">
                                {league.description}
                              </p>
                            ) : null}
                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500">
                              <span>
                                {league.teamCount} team
                                {league.teamCount !== 1 ? 's' : ''}
                              </span>
                              {league.leagueSeason?.draftStatus ? (
                                <>
                                  <span aria-hidden="true">•</span>
                                  <span>
                                    Draft: {league.leagueSeason.draftStatus}
                                  </span>
                                </>
                              ) : null}
                              {league.leagueSeason?.invitationCode ? (
                                <>
                                  <span aria-hidden="true">•</span>
                                  <span>
                                    Code: {league.leagueSeason.invitationCode}
                                  </span>
                                </>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 sm:flex-shrink-0 pl-12 sm:pl-0">
                          <Link
                            to={`/admin/leagues/${league.id}/seasons/${seasonId}`}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                          >
                            View Teams
                          </Link>
                          <Link
                            to={`/admin/leagues/${league.id}`}
                            className="text-gray-400 hover:text-gray-600 hidden sm:block"
                            aria-label={`View ${league.name}`}
                          >
                            <svg
                              className="h-5 w-5"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                              aria-hidden="true"
                            >
                              <path
                                fillRule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </Link>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Score Recalculation Section */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <TrophyIcon className="h-6 w-6 text-gray-400 mr-2" />
                <h2 className="text-lg font-medium text-gray-900">Score Management</h2>
              </div>
            </div>
            <div className="px-6 py-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Recalculate scores for all teams across all leagues using this season. This will:
                  </p>
                  <ul className="text-sm text-gray-600 mb-4 list-disc list-inside space-y-1">
                    <li>Calculate survival points for drafted contestants who survive each week</li>
                    <li>Award prediction points for correct weekly predictions (pre-merge vs post-merge)</li>
                    <li>Give winner points for correctly predicting the ultimate winner</li>
                    <li>Use each league's specific scoring rules as configured by their admins</li>
                  </ul>
                  
                  {recalculationResult && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <TrophyIcon className="h-5 w-5 text-green-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-green-800">
                            {recalculationResult.week ? `Week ${recalculationResult.week} Score Calculation Complete` : 'Score Recalculation Complete'}
                          </h3>
                          <div className="mt-2 text-sm text-green-700">
                            <p>Updated {recalculationResult.teamsUpdated} teams across {recalculationResult.leaguesProcessed} leagues.</p>
                            {recalculationResult.week && (
                              <p className="text-xs text-green-600 mt-1">
                                Only Week {recalculationResult.week} scores were recalculated.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={handleRecalculateScores}
                    disabled={recalculating}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {recalculating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Recalculating...
                      </>
                    ) : (
                      <>
                        <TrophyIcon className="h-4 w-4 mr-2" />
                        Recalculate Scores
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showContestantModal && (
        <ContestantModal
          contestant={selectedContestant}
          seasonId={seasonId}
          isEditing={editingContestant}
          onClose={() => setShowContestantModal(false)}
          onSave={handleContestantSaved}
        />
      )}

      {showWeekModal && (
        <WeekModal
          week={selectedWeek}
          seasonId={seasonId}
          existingWeeks={season.weeks || []}
          onClose={() => setShowWeekModal(false)}
          onSave={handleWeekSaved}
        />
      )}

    </div>
  );
}
